import React from "react";
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Slider
} from "antd";
import i18n from "i18next";
import ColorPicker from "../../tools/common/ColorPicker.js";
import { ICONS } from "../../../constants/Icons";

const FORM_LAYOUT = {
  labelCol: { span: 12 },
  wrapperCol: { span: 12 },
};

const DEFAULT_COLOR = "rgba(0, 0, 0, 1)";
const TRANSPARENT_COLOR = "rgba(255, 255, 255, 0)";

const FONT_SIZE_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const value = `${index + 4}`;

  return (
    <Select.Option key={value} value={value}>
      {index + 4}
    </Select.Option>
  );
});

const numberRules = [
  {
    type: "number",
    message: i18n.t("validations.messageInput"),
    min: 1,
  },
];

const ShapePropertiesPanel = ({ form, selectedItem, canvasRef }) => {
  const objectGroup = selectedItem;
  const objectShape = objectGroup._objects[0];
  const objectText = objectGroup._objects[1];
  const objectShapeIcon = objectGroup._objects[2];

  const isEllipse = objectGroup.superType === "ellipse";

  const renderFormItem = (label, name, options, component) => (
    <Form.Item label={label} {...FORM_LAYOUT}>
      {form(name, options)(component)}
    </Form.Item>
  );

  const renderNumberSizeFields = () => {
    const widthName = isEllipse ? "rx" : "width";
    const heightName = isEllipse ? "ry" : "height";

    return (
      <>
        {renderFormItem(
          i18n.t("propertyBar.shapes.width"),
          widthName,
          {
            rules: numberRules,
            initialValue: objectShape[widthName],
          },
          <InputNumber min={1} />
        )}

        {renderFormItem(
          i18n.t("propertyBar.shapes.height"),
          heightName,
          {
            rules: numberRules,
            initialValue: objectShape[heightName],
          },
          <InputNumber min={1} />
        )}
      </>
    );
  };

  return (
    <Row>
      <Col span={24} className="mb-3">
        <h3>{i18n.t("propertyBar.shapes.title")}</h3>

        {renderNumberSizeFields()}

        {renderFormItem(
          i18n.t("propertyBar.shapes.opacity"),
          "opacity",
          { initialValue: objectShape.opacity },
          <Slider min={0} max={1} step={0.1} />
        )}

        {renderFormItem(
          i18n.t("propertyBar.shapes.angle"),
          "angle",
          {
            rules: [{ type: "number", min: -180, max: 180 }],
            initialValue: objectGroup.angle,
          },
          <Slider min={-180} max={180} step={1} />
        )}

        {renderFormItem(
          i18n.t("propertyBar.shapes.fillColor"),
          "fill",
          { initialValue: objectShape.fill || DEFAULT_COLOR },
          <ColorPicker />
        )}

        {renderFormItem(
          i18n.t("propertyBar.shapes.strokeWidth"),
          "strokeWidthShape",
          { initialValue: objectShape.strokeWidth || 1 },
          <InputNumber min={1} max={30} />
        )}

        {renderFormItem(
          i18n.t("propertyBar.shapes.borderColor"),
          "stroke",
          { initialValue: objectShape.stroke || TRANSPARENT_COLOR },
          <ColorPicker />
        )}
      </Col>

      <Col span={24}>
        {renderFormItem(
          "Mostrar texto",
          "visibleText",
          {
            rules: [{ type: "boolean" }],
            valuePropName: "checked",
            initialValue: objectGroup.visibleText,
          },
          <Checkbox />
        )}
      </Col>

      {objectGroup.visibleText && (
        <Col span={24}>
          {renderFormItem(
            i18n.t("propertyBar.shapes.label"),
            "textText",
            { initialValue: objectText.text },
            <Input />
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.left"),
            "leftText",
            { initialValue: objectText.left },
            (
              <Slider
                step={1}
                min={-(objectShape.width / 2)}
                max={objectShape.width / 2}
              />
            )
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.top"),
            "topText",
            { initialValue: objectText.top },
            (
              <Slider
                step={1}
                min={-(objectShape.height / 2)}
                max={objectShape.height / 2}
              />
            )
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.fontSize"),
            "fontSizeText",
            { initialValue: `${objectText.fontSize || 16}` },
            <Select>{FONT_SIZE_OPTIONS}</Select>
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.fontColor"),
            "fillText",
            { initialValue: objectText.fill || DEFAULT_COLOR },
            <ColorPicker />
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.angle"),
            "angleText",
            { initialValue: objectText.angle },
            <Slider min={-180} max={180} />
          )}
        </Col>
      )}

      <Col span={24}>
        <h3>{i18n.t("propertyBar.icon.name")}</h3>
      </Col>

      <Col span={24}>
        {renderFormItem(
          "Mostrar icono",
          "visibleIcon",
          {
            rules: [{ type: "boolean" }],
            valuePropName: "checked",
            initialValue: objectGroup.visibleIcon,
          },
          <Checkbox />
        )}
      </Col>

      {objectGroup.visibleIcon && (
        <Col span={24}>
          {renderFormItem(
            i18n.t("propertyBar.shapes.iconType"),
            "typeIconIcon",
            { initialValue: objectShapeIcon.svg },
            (
              <Select>
                {ICONS.map(({ id, name }) => (
                  <Select.Option key={id} value={id}>
                    {i18n.t(name)}
                  </Select.Option>
                ))}
              </Select>
            )
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.left"),
            "leftIcon",
            { initialValue: objectShapeIcon.left },
            (
              <Slider
                step={1}
                min={-(objectShape.width / 2)}
                max={
                  objectShape.width / 2 -
                  objectShapeIcon.originWidth * objectShapeIcon.scaled
                }
              />
            )
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.top"),
            "topIcon",
            { initialValue: objectShapeIcon.top },
            (
              <Slider
                step={1}
                min={-(objectShape.height / 2)}
                max={
                  objectShape.height / 2 -
                  objectShapeIcon.originHeight * objectShapeIcon.scaled
                }
              />
            )
          )}

          {renderFormItem(
            i18n.t("propertyBar.icon.fontIcon"),
            "scaled",
            { initialValue: objectShapeIcon.scaled },
            <Slider min={0.1} step={0.1} max={2.1} />
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.color"),
            "fillIcon",
            { initialValue: objectShapeIcon.fill || DEFAULT_COLOR },
            <ColorPicker />
          )}

          {renderFormItem(
            i18n.t("propertyBar.shapes.angle"),
            "rotateIcon",
            { initialValue: objectShapeIcon.rotated },
            <Slider step={1} min={0} max={360} />
          )}
        </Col>
      )}
    </Row>
  );
};

export { ShapePropertiesPanel };